import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bike, Zap } from 'lucide-react';
import { Button } from './ui/button';

const VEHICLE_VIDEOS = {
    bicycle: '/videos/skateboard.mp4',
    ebike: '/videos/electric_dirtbike.mp4',
    skateboard: '/videos/skateboard.mp4',
    electric_skateboard: '/videos/electric_longboard.mp4',
    onewheel: '/videos/euc.mp4',
    euc: '/videos/euc.mp4',
    dirtbike: '/videos/gas_dirtbike.mp4',
    default: '/videos/surfer.mp4',
};

const PARTICLES = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 1.5,
    duration: 2 + Math.random() * 2,
    size: 3 + Math.random() * 6,
    color: ['#1E90FF', '#FF33CC', '#FFD700', '#00BFFF'][Math.floor(Math.random() * 4)],
}));

export default function RideComplete({ rideResult, vehicleType, onClose }) {
    const videoRef = useRef(null);
    const [phase, setPhase] = useState('intro');
    const [videoReady, setVideoReady] = useState(false);

    const videoSrc = VEHICLE_VIDEOS[vehicleType] || VEHICLE_VIDEOS.default;
    const passed = rideResult?.validation?.passed;

    useEffect(() => {
        const t1 = setTimeout(() => setPhase('slowmo'), 300);
        const t2 = setTimeout(() => setPhase('stats'), 2500);
        const t3 = setTimeout(() => setPhase('done'), 4000);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, []);

    useEffect(() => {
        if (!videoRef.current || !videoReady) return;
        if (phase === 'slowmo') videoRef.current.playbackRate = 0.3;
        else if (phase === 'stats') videoRef.current.playbackRate = 1.0;
    }, [phase, videoReady]);

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="ride-complete-modal">
            <video
                ref={videoRef}
                src={videoSrc}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay muted playsInline loop
                onCanPlay={() => { setVideoReady(true); if (videoRef.current) videoRef.current.playbackRate = 0.3; }}
                style={{ opacity: videoReady ? 1 : 0, transition: 'opacity 0.5s' }}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30 z-10" />
            <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_30%,rgba(0,0,0,0.8)_100%)] z-10" />

            {/* Particles */}
            <div className="absolute inset-0 z-20 overflow-hidden pointer-events-none">
                {phase !== 'intro' && PARTICLES.map((p) => (
                    <motion.div
                        key={p.id}
                        className="absolute rounded-full"
                        style={{
                            left: `${p.x}%`,
                            width: p.size, height: p.size,
                            backgroundColor: p.color,
                            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
                        }}
                        initial={{ bottom: '-10%', opacity: 0 }}
                        animate={{ bottom: '110%', opacity: [0, 1, 1, 0] }}
                        transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'linear' }}
                    />
                ))}
            </div>

            <div className="relative z-30 text-center px-6 max-w-lg">
                {phase === 'slowmo' && (
                    <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} className="mb-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 backdrop-blur-md">
                            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                            <span className="text-primary text-sm font-display tracking-[0.2em] uppercase">
                                Ride Complete
                            </span>
                        </div>
                    </motion.div>
                )}

                {(phase === 'stats' || phase === 'done') && (
                    <>
                        <motion.div
                            initial={{ opacity: 0, scale: 0, rotate: -20 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                            className="mb-6"
                        >
                            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center border ${
                                passed ? 'bg-neon-green/20 shadow-[0_0_40px_rgba(0,230,77,0.4)] border-neon-green/30' 
                                       : 'bg-neon-gold/20 shadow-[0_0_40px_rgba(255,215,0,0.4)] border-neon-gold/30'
                            }`}>
                                <Bike className={`w-12 h-12 ${passed ? 'text-neon-green' : 'text-neon-gold'}`} />
                            </div>
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-3xl sm:text-5xl font-black font-display gradient-text mb-2"
                        >
                            {passed ? 'RIDE VALIDATED' : 'UNDER REVIEW'}
                        </motion.h2>

                        {/* Stats Grid */}
                        <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="mt-6 grid grid-cols-3 gap-3"
                        >
                            <div className="bg-black/40 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/10">
                                <span className="text-[10px] text-white/40 font-display tracking-[0.2em] uppercase">Distance</span>
                                <p className="text-xl font-black font-mono text-foreground mt-1">
                                    {rideResult?.ride?.distance?.toFixed(2) || '0'}<span className="text-xs text-white/40 ml-1">km</span>
                                </p>
                            </div>
                            <div className="bg-black/40 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/10">
                                <span className="text-[10px] text-white/40 font-display tracking-[0.2em] uppercase">Speed</span>
                                <p className="text-xl font-black font-mono text-foreground mt-1">
                                    {rideResult?.validation?.averageSpeed || '0'}<span className="text-xs text-white/40 ml-1">km/h</span>
                                </p>
                            </div>
                            <div className="bg-black/40 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/10">
                                <span className="text-[10px] text-white/40 font-display tracking-[0.2em] uppercase">Multiplier</span>
                                <p className="text-xl font-black font-mono text-primary mt-1">
                                    {rideResult?.validation?.rewardMultiplier || '1'}x
                                </p>
                            </div>
                        </motion.div>

                        {/* Boltz Earned */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.6, type: 'spring', stiffness: 150 }}
                            className="mt-6"
                        >
                            <div className="inline-flex flex-col items-center gap-1 bg-black/40 backdrop-blur-xl rounded-2xl px-10 py-5 border border-neon-gold/20 shadow-[0_0_30px_rgba(255,215,0,0.15)]">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-5 h-5 text-neon-gold" />
                                    <span className="text-xs text-neon-gold/70 font-display tracking-[0.3em] uppercase">Earned</span>
                                </div>
                                <span className="text-5xl font-black font-display text-neon-gold text-glow-gold" data-testid="ride-reward">
                                    +{rideResult?.boltzEarned?.toFixed(2) || '0'}
                                </span>
                                <span className="text-lg font-bold font-display text-neon-gold/70">Boltz</span>
                            </div>
                        </motion.div>
                    </>
                )}

                {phase === 'done' && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8">
                        <Button
                            onClick={onClose}
                            className="px-10 py-6 rounded-full bg-gradient-to-r from-primary to-cyan-glow text-white font-bold text-lg hover:scale-105 hover:shadow-[0_0_30px_rgba(30,144,255,0.5)] transition-all duration-300"
                            style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.05em' }}
                            data-testid="ride-complete-continue-btn"
                        >
                            Continue
                        </Button>
                    </motion.div>
                )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-neon-gold to-transparent z-30 opacity-60" />
        </div>
    );
}
